/* eslint-disable @typescript-eslint/no-explicit-any */

import { LinkField, type Field } from "@sitecore-content-sdk/nextjs";
import { type TaxonomyItem } from "./Search/TaxonomyItem";

export type CoveoStaticFetchParams = {
  cq?: Field<string>;
  aq?: Field<string>;
  lq?: Field<string>;
  dq?: Field<string>;
  SortCriteria?: Field<string>;
  NumberOfResults?: Field<number>;
  FieldsToInclude?: Field<string>;
  SearchHub: Field<string>;
};

export interface CustomEnumOption {
  displayName: string;
  fields: {
    Value: {
      value: string;
    };
  };
}

export type CoveoConfigOptions = {
  Applications?: TaxonomyItem[];
  Solutions?: TaxonomyItem[];
  Industries?: TaxonomyItem[];
  Markets?: TaxonomyItem[];
  TemplateTypes?: CustomEnumOption[];
  BlogArticleType?: TaxonomyItem[];
  BlogArticleTopic?: TaxonomyItem[];
  BlogArticleAuthor?: TaxonomyItem[];
  NewsLetterName?: TaxonomyItem[];
  NewsType?: TaxonomyItem[];
  NewsTopic?: TaxonomyItem[];
  StoryType?: TaxonomyItem[];
  StoryTopic?: TaxonomyItem[];
  EventsType?: CustomEnumOption[];
  LocationType?: TaxonomyItem[];
  LocationRegion?: TaxonomyItem[];
  Companies?: TaxonomyItem[];
  OfferingType?: TaxonomyItem[];
  TranslationOwner?: TaxonomyItem[];
  GoogleAnalyticsRepGroup?: TaxonomyItem[];
  Countries?: TaxonomyItem[];
  Regions?: TaxonomyItem[];
  RegionsAndCountries?: TaxonomyItem[];
  IndustriesAndMarkets?: TaxonomyItem[];
  ApplicationsAndSolutions?: TaxonomyItem[];
  SortField?: Field<string>;
  SoftFieldDirection?: Field<string>;
  ContextTaxonomies?: CoveoContextTaxonomy[];
};

export type CoveoContextTaxonomy = {
  id: string;
  url: string;
  name: string;
  fields: {
    Value: Field<string>;
  };
};

export interface CoveoSearchResult {
  title?: string;
  uri?: string;
  printableUri?: string;
  clickUri?: string;
  uniqueId?: string;
  excerpt?: string;
  firstSentences?: string | null;
  summary?: string | null;
  flags?: string;
  hasHtmlVersion?: boolean;
  hasMobileHtmlVersion?: boolean;
  score?: number;
  percentScore?: number;
  rankingInfo?: any | null;
  rating?: number;
  isTopResult?: boolean;
  isRecommendation?: boolean;
  isUserActionView?: boolean;
  titleHighlights?: any[];
  firstSentencesHighlights?: any[];
  excerptHighlights?: any[];
  printableUriHighlights?: any[];
  summaryHighlights?: any[];
  parentResult?: any | null;
  childResults?: any[];
  totalNumberOfChildResults?: number;
  absentTerms?: any[];
  raw?: CoveoSearchResultRaw;
  Title?: string;
  Uri?: string;
  PrintableUri?: string;
  ClickUri?: string;
  UniqueId?: string;
  Excerpt?: string;
  FirstSentences?: string | null;
}

export interface CoveoSearchResultRaw {
  systitle?: string;
  sysurihash?: string;
  urihash?: string;
  sysuri?: string;
  sitemappriority?: number;
  systransactionid?: number;
  pagetypeid?: string;
  pageid?: string;
  sysconcepts?: string;
  pagerefimage?: string;
  concepts?: string;
  documenttype?: string;
  sysindexeddate?: number;
  permanentid?: string;
  syslanguage?: string[] | null;
  searchable?: string;
  transactionid?: number;
  langiso?: string;
  title?: string;
  date?: number;
  sitemapchangefrequency?: string;
  src?: string;
  taxonomy_industry?: string[] | null;
  sourcetype?: string;
  sysconnectortype?: string;
  pagetitle?: string;
  rowid?: number;
  taxonomy_technology?: string[] | null;
  size?: number;
  sysdocumenttype?: string;
  pagetype?: string;
  clickableuri?: string;
  syssource?: string;
  orderingid?: number;
  syssize?: number;
  sysdate?: number;
  taxonomy_topic?: string[] | null;
  primaryid?: string;
  wordcount?: number;
  source?: string;
  sitemaplastmodified?: number;
  pagetag?: string;
  taxonomy_services?: string[] | null;
  collection?: string;
  syssourcetype?: string;
  pagereftext?: string;
  indexeddate?: number;
  connectortype?: string;
  filetype?: string;
  sysclickableuri?: string;
  sysfiletype?: string;
  language?: string[] | null;
  sitemapparenturl?: string;
  taxonomy_geography?: string[] | null;
  pagereftitle?: string;
  taxonomy_year?: number;
  sysrowid?: number;
  uri?: string;
  syscollection?: string;
}

export interface CoveoSearchApiQuery {
  locale?: string;
  searchHub?: string;
  tab?: string;
  cq?: string;
  aq?: string;
  dq?: string;
  lq?: string;
  q?: string;
  groupBy?: CoveoSearchApiQueryGroupBy[];
  sortCriteria?: string;
  numberOfResults?: number;
  fieldsToInclude?: string[];
  pipeline?: string;
}

export interface CoveoSearchApiQueryGroupBy {
  field: string;
  sortCriteria: string;
}

export interface CoveoSearchApiResponse {
  totalCount: number;
  totalCountFiltered: number;
  duration: number;
  indexDuration: number;
  requestDuration: number;
  searchUid: string;
  pipeline: string;
  apiVersion: number;
  basicExpression: string;
  advancedExpression: null | string;
  largeExpression: null | string;
  constantExpression: string;
  disjunctionExpression: null | string;
  mandatoryExpression: null | string;
  userIdentities: CoveoSearchApiUserIdentity[];
  rankingExpressions: any[];
  topResults: any[];
  index: string;
  indexRegion: string;
  indexToken: string;
  refinedKeywords: any[];
  triggers: any[];
  termsToHighlight: Record<string, any>;
  phrasesToHighlight: Record<string, any>;
  queryCorrections: any[];
  groupByResults: any[];
  facets: any[];
  suggestedFacets: any[];
  categoryFacets: any[];
  results: CoveoSearchApiCareersResult[];
  extendedResults: Record<string, any>;
}

export interface CoveoSearchPagesApiResponse {
  totalCount: number;
  totalCountFiltered: number;
  duration: number;
  indexDuration: number;
  requestDuration: number;
  searchUid: string;
  pipeline: string;
  apiVersion: number;
  basicExpression: string;
  advancedExpression: null | string;
  largeExpression: null | string;
  constantExpression: string;
  disjunctionExpression: null | string;
  mandatoryExpression: null | string;
  userIdentities: CoveoSearchApiUserIdentity[];
  rankingExpressions: any[];
  topResults: any[];
  index: string;
  indexRegion: string;
  indexToken: string;
  refinedKeywords: any[];
  triggers: any[];
  termsToHighlight: Record<string, any>;
  phrasesToHighlight: Record<string, any>;
  queryCorrections: any[];
  groupByResults: any[];
  facets: any[];
  suggestedFacets: any[];
  categoryFacets: any[];
  results: CoveoSearchResult[];
  extendedResults: Record<string, any>;
}

export interface CoveoSearchApiCareersResult {
  title: string;
  uri: string;
  printableUri: string;
  clickUri: string;
  uniqueId: string;
  excerpt: string;
  firstSentences: null | string;
  summary: null | string;
  flags: string;
  hasHtmlVersion: boolean;
  hasMobileHtmlVersion: boolean;
  score: number;
  percentScore: number;
  rankingInfo: null | string;
  rating: number;
  isTopResult: boolean;
  isRecommendation: boolean;
  isUserActionView: boolean;
  titleHighlights: any[];
  firstSentencesHighlights: any[];
  excerptHighlights: any[];
  printableUriHighlights: any[];
  summaryHighlights: any[];
  parentResult: null | string;
  childResults: any[];
  totalNumberOfChildResults: number;
  absentTerms: any[];
  // raw: CoveoSearchApiCareersFields | CoveoSearchApiMasterDataFields;
  raw: any;
}

export interface CoveoSearchApiCareersFields {
  systitle: string;
  sysurihash: string;
  urihash: string;
  sysuri: string;
  systransactionid: number;
  sysconcepts: string;
  concepts: string;
  sysindexeddate: number;
  permanentid: string;
  syslanguage: string[];
  transactionid: number;
  title: string;
  date: number;
  jobareaofexpertise: string;
  jobdescription: string;
  jobname: string;
  sysconnectortype: string;
  rowid: number;
  size: number;
  jobposteddate: number;
  jobcountry: string;
  sitecorecountry: string;
  syssource: string;
  orderingid: number;
  syssize: number;
  sysdate: number;
  jobid: number;
  jobidstring: string;
  isignoredjob: number;
  wordcount: number;
  source: string;
  collection: string;
  indexeddate: number;
  connectortype: string;
  filetype: string;
  jobcityname: string;
  sysfiletype: string;
  language: string[];
  sysrowid: number;
  uri: string;
  syscollection: string;
  jobgreatplacetowork: string;
  jobwhatyouwilldo: string;
  joblearnmore: string;
  jobenjoyyourcareer: string;
  jobcomejoinus: string;
  jobskillsandexperiences: string;
  jobaboutyou: string;
}

export interface CoveoSearchApiMasterDataFields {
  systitle: string;
  job_expertise_language_id: number;
  sysurihash: string;
  urihash: string;
  sysuri: string;
  systransactionid: number;
  sysindexeddate: number;
  job_area_of_expertise: string;
  permanentid: string;
  transactionid: number;
  title: string;
  date: number;
  jobareaofexpertise: string;
  sysconnectortype: string;
  rowid: number;
  size: number;
  job_expertise_id: number;
  job_location_type: string;
  job_location_id: number;
  job_location_name: string;
  syssource: string;
  orderingid: number;
  syssize: number;
  sysdate: number;
  wordcount: number;
  source: string;
  collection: string;
  job_item_type: string;
  indexeddate: number;
  connectortype: string;
  filetype: string;
  sysfiletype: string;
  sysrowid: number;
  uri: string;
  syscollection: string;
}

export interface CoveoSearchApiUserIdentity {
  name: string;
  provider: string;
  type: string;
}

export interface CoveoValuesReposponse {
  values: CoveoValueResponse[];
}

export interface CoveoValueResponse {
  value: string;
  lookupValue: string;
  numberOfResults: number;
}

export type CoveoCardsFields = {
  Title: Field<string>;
  SubTitle: Field<string>;
  Link: LinkField;
  ShowMoreText?: Field<string>;
  ResultsNotFoundText?: Field<string>;
} & CoveoStaticFetchParams &
  CoveoConfigOptions;

export type CoveoCardsProps = {
  params: { [key: string]: string };
  fields: CoveoCardsFields;
  cards: CoveoCard[];
  isCardImageExists?: boolean;
};

export type CoveoCard = {
  title: string;
  desc: string;
  image: string;
  link: string;
  linkLabel: string;
  date?: string | null;
  subtitle?: string;
  timezone?: string;
};

export enum TaxonomyContext {
  Applications = "Applications",
  Solutions = "Solutions",
  Industries = "Industries",
  Markets = "Markets",
  Regions = "Regions",
  Countries = "Countries",
  Companies = "Companies",
  ArticleAuthor = "Article Author",
  ArticleTopic = "Article Topic",
  ArticleType = "Article Type",
  EventsType = "Events Type",
  LocationRegion = "Location Region",
  LocationType = "Location Type",
  NewsTopic = "News Topic",
  NewsType = "News Type",
  StoryTopic = "Story Topic",
  StoryType = "Story Type",
  OfferingType = "Offering Type",
  GA4RepGroup = "GA4 Rep Group",
  TranslationOwner = "Translation Owner",
}
